import { TextButton } from "@/modules/common/components/ui/text-button";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import { useCustomerIdInfo } from "@/modules/property-tracks/hooks/customer-id-info/use-customer-id-info.hook";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { PencilLine } from "lucide-react";
import { useState } from "react";
import EditCustomerIdInfoModal from "./edit-customer-id-info-modal";

const formatValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return String(value);
};

const CustomerIdInfoTab = ({ customerUid }: { customerUid: string }) => {
  const theme = useTheme();
  const { readonly } = useReadonlyContext();
  const [modalOpen, setModalOpen] = useState(false);
  const { data: info, isLoading } = useCustomerIdInfo({ customerUid });

  if (isLoading) {
    return <Skeleton variant="rectangular" width="100%" height={400} />;
  }

  if (!info) {
    return (
      <Box sx={{ padding: theme.nawy.spacing.lg }}>
        <Typography color={theme.nawy.colors.semantic.text.secondary}>
          No customer ID info available.
        </Typography>
      </Box>
    );
  }

  const fields: { label: string; value?: string | number | null }[] = [
    { label: "ID Type", value: info.idType },
    { label: "Full Name", value: info.fullName },
    { label: "Surname", value: info.surname },
    { label: "Given Names", value: info.givenNames },
    { label: "ID Number", value: info.idNumber },
    { label: "Nationality", value: info.nationality },
    { label: "Issuing Country", value: info.issuingCountry },
    { label: "Gender", value: info.gender },
    { label: "Religion", value: info.religion },
    { label: "Address", value: info.address },
    { label: "Birth Date", value: info.birthDate },
    { label: "Expiry Date", value: info.expiryDate },
  ];

  return (
    <Box
      sx={{
        padding: theme.nawy.spacing.lg,
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Customer ID Info
        </Typography>
        <TextButton
          disabled={readonly}
          onClick={() => setModalOpen(true)}
          sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
        >
          <PencilLine size={16} /> Edit
        </TextButton>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: theme.nawy.spacing.md,
        }}
      >
        {fields.map(({ label, value }) => (
          <Box
            key={label}
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            <Typography
              variant="caption"
              color={theme.nawy.colors.semantic.text.secondary}
            >
              {label}
            </Typography>
            <Typography variant="body2">{formatValue(value)}</Typography>
          </Box>
        ))}
      </Box>

      {modalOpen && (
        <EditCustomerIdInfoModal
          info={info}
          open={modalOpen}
          setOpen={setModalOpen}
        />
      )}
    </Box>
  );
};

export default CustomerIdInfoTab;
